/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import type { PlaybackError, Player, PlaylistController } from '@amzn/bf-playback'
import {
    createDisposer,
    ErrorLevel,
    ErrorOrigin,
    isReportableError,
    isSilentError,
    logError,
    noop,
    RequestError,
    type Unsubscribe,
} from '@amazon/vinyl-util'
import { ctx } from '@/util/context'
import { formatError, handleError } from '@/errorHandler'
import { toastError } from '@/components/toast'
import { getStringAsync } from '@/playback/i18n/bundle.generated'
import { toastPlaybackFailed } from '@/playback/playbackNoticeToast'
import {
    clearFailedPlayback,
    endPlaybackStart,
    takeFailedPlaybackStart,
    type PlaybackStartFailureDeps,
} from '@/playback/playbackStartFailure'
import { onPipClose, setHasVideo } from '@/video/videoStage'
import { videoOnDemandEnabled } from '@/video/videoOnDemand'
import { navigateTo } from '@/router/router'
import { reportNetworkError } from '@/offline/createGoodNetworkObservable'
import { fireUpsell } from '@/messaging/dm/upsellFire'
import { downloadPlaybackFailureKey } from '@/downloads/downloadPlaybackFailure'

/**
 * Whether an active PiP window should close now that the current entry
 * changed. Only a switch to *different* media closes it — a re-queue of
 * the same asin (e.g. a re-buffer that re-emits `current`) leaves the
 * floating window intact.
 */
export function shouldExitPipOnMediaChange(
    pipActive: boolean,
    previousMediaId: string | null,
    nextMediaId: string | null
): boolean {
    return pipActive && nextMediaId !== previousMediaId
}

export type PlayerErrorKind = 'ignored' | 'network' | 'unrecoverable' | 'unclassified'

/**
 * Routes a Vinyl error the way GH does: a `serviceInternal` failure with no response is a network
 * blip, `fatal` or `serviceExternal` is unrecoverable, and a silent abort is nothing. A request
 * that will retry is not final yet; an error that is not Vinyl's carries no classification.
 */
export function classifyPlayerError(error: unknown): PlayerErrorKind {
    if (!isReportableError(error)) return 'unclassified'
    if (isSilentError(error)) return 'ignored'
    const { response } = error as { readonly response?: unknown }
    if (error.origin === ErrorOrigin.SERVICE_INTERNAL && response == null) return 'network'
    if (error instanceof RequestError && error.info.willRetry) return 'ignored'
    return error.level === ErrorLevel.FATAL || error.origin === ErrorOrigin.SERVICE_EXTERNAL
        ? 'unrecoverable'
        : 'ignored'
}

const { target, errorHandler } = ctx('playbackUiBinding')

function playerErrorCause(err: PlaybackError): unknown {
    return err.cause ?? new Error(`${err.code}: ${err.message}`)
}

async function openVideoStage(): Promise<void> {
    if (await videoOnDemandEnabled()) navigateTo('/now-playing')
}

export interface PlaybackUiBindingsDeps extends PlaybackStartFailureDeps {
    readonly playerPromise: Promise<Player>
    readonly playlistController: PlaylistController
}

/**
 * The web view's half of the playlist ↔ player glue: the video stage and PiP window follow the
 * current entry (a music video starting raises the now-playing stage, as GH's
 * VideoPlaybackInitiated does, except on a reload restore), and a player error raises the offline state or toasts. The engine half
 * (`startPlaybackSession`) runs with the engines.
 */
export function initializePlaybackUiBindings(deps: PlaybackUiBindingsDeps): Unsubscribe {
    const disposer = createDisposer()
    const { add, dispose } = disposer
    const { playerPromise, playlistController } = deps

    // Native PiP X-button → unload the playlist. Without this, dismissing
    // the floating window would only exit PiP and the audio/video would
    // keep playing in the transport — the user's intent ("close this")
    // is clearly to stop, not to keep playing in the background.
    add(
        onPipClose(() => {
            playlistController.clear()
        })
    )

    // Starting different media while the video is handed off to PiP closes
    // the floating window — the old picture is no longer what's playing.
    let pipMediaId: string | null = playlistController.current.value?.entity.id ?? null
    add(
        playlistController.current.onData((entry) => {
            const id = entry?.entity.id ?? null
            if (shouldExitPipOnMediaChange(!!document.pictureInPictureElement, pipMediaId, id)) {
                document.exitPictureInPicture().catch(noop)
            }
            pipMediaId = id
        })
    )

    playerPromise
        .then((player) => {
            if (disposer.disposed) return
            add(player.playing$.onData((playing) => playing && endPlaybackStart()))
            let initialEmission = true
            add(
                playlistController.current.onData((entry) => {
                    const isVideo = entry?.video === true
                    setHasVideo(isVideo)
                    const raiseStage =
                        isVideo && !initialEmission && !playlistController.restoringCurrent
                    initialEmission = false
                    if (raiseStage) openVideoStage().catch(errorHandler)
                })
            )
            // A computer file that can't play says so and gives way to the next song.
            const skipUnplayableFile = (err: PlaybackError) => {
                endPlaybackStart()
                handleError(target, playerErrorCause(err))
                toastError(
                    import('@/localFiles/i18n/bundle.generated').then((m) =>
                        m.getStringAsync('dmusic_local_file_unplayable')
                    )
                )
                playlistController.skipNext().catch(errorHandler)
            }
            // An error can land inside a `current` notification, which `current` cannot be set from.
            const clearAfterNotify = (clear: () => void) =>
                queueMicrotask(() => {
                    if (!disposer.disposed) clear()
                })
            // A load/decode failure surfaces on the `error$` cell, not a rejected command promise.
            add(
                player.error$.onData((err) => {
                    if (!err) return
                    if (err.code === 'localPlaybackFailed') {
                        skipUnplayableFile(err)
                        return
                    }
                    const kind = classifyPlayerError(err.cause)
                    if (kind === 'ignored') return
                    if (kind === 'network') {
                        logError(target, 'player error', formatError(err.cause ?? err))
                        reportNetworkError()
                        return
                    }
                    handleError(target, playerErrorCause(err))
                    const downloadFailure = downloadPlaybackFailureKey(err.cause)
                    if (downloadFailure) {
                        endPlaybackStart()
                        // Morpho raises the plan's upsell for a download the plan no longer covers.
                        if (
                            downloadFailure === 'dmusic_download_not_on_plan' &&
                            fireUpsell('ineligibleContent')
                        )
                            return
                        // The downloads strings load only here, off the boot bundle.
                        toastError(
                            import('@/downloads/i18n/bundle.generated').then((m) =>
                                m.getStringAsync(downloadFailure)
                            )
                        )
                        return
                    }
                    const failedStart = takeFailedPlaybackStart()
                    if (failedStart) {
                        toastPlaybackFailed(failedStart.mediaType)
                        clearAfterNotify(() => clearFailedPlayback(deps))
                        return
                    }
                    toastError(getStringAsync('dmusic_playback_unexpected_error_toast'))
                    if (kind === 'unrecoverable') clearAfterNotify(() => playlistController.clear())
                })
            )
        })
        .catch(errorHandler)
    return dispose
}
